window.AdminProducts = function AdminProducts({ showNotification }) {
  const [products, setProducts] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('All');
  
  // Product Form Modal state
  const [showModal, setShowModal] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [modalTab, setModalTab] = React.useState('general'); // 'general', 'applications', 'parameters', 'workflow', 'specs', 'sensors', 'media'

  const [form, setForm] = React.useState({
    id: 0,
    product_code: '',
    name: '',
    category_id: 1,
    badge: 'High Precision',
    short_description: '',
    full_description: '',
    applications: [],
    testingParameters: [],
    howItWorks: [],
    specs: {},
    sensorsInstrumentation: [],
    automationSoftware: [],
    standardsCompliance: [],
    primary_image: '',
    gallery: [],
    datasheet_url: '',
    status: 'published',
    display_order: 0
  });

  // Helpers for array/object inputs
  const [newAppInput, setNewAppInput] = React.useState('');
  const [newParamName, setNewParamName] = React.useState('');
  const [newParamUnit, setNewParamUnit] = React.useState('');
  const [newParamIcon, setNewParamIcon] = React.useState('speed');
  const [newStepTitle, setNewStepTitle] = React.useState('');
  const [newStepDesc, setNewStepDesc] = React.useState('');
  const [specKey, setSpecKey] = React.useState('');
  const [specVal, setSpecVal] = React.useState('');
  const [newSensorInput, setNewSensorInput] = React.useState('');
  const [newAutoInput, setNewAutoInput] = React.useState('');
  const [newStandardInput, setNewStandardInput] = React.useState('');
  const [newGalleryInput, setNewGalleryInput] = React.useState('');

  const imageUploadRef = React.useRef(null);
  const galleryUploadRef = React.useRef(null);
  const datasheetUploadRef = React.useRef(null);
  const [mediaPickerConfig, setMediaPickerConfig] = React.useState({ isOpen: false, onSelect: null, title: 'Choose Image' });
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [uploadingGallery, setUploadingGallery] = React.useState(false);
  const [uploadingDatasheet, setUploadingDatasheet] = React.useState(false);

  React.useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // Lock body and main content scroll when modal is open
  React.useEffect(() => {
    const mainEl = document.querySelector('main');
    if (showModal) {
      document.body.style.overflow = 'hidden';
      if (mainEl) mainEl.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      if (mainEl) mainEl.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'unset';
      if (mainEl) mainEl.style.overflow = 'auto';
    };
  }, [showModal]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('products.php?admin=1');
      if (res.success && res.data && res.data.length > 0) {
        setProducts(res.data);
      } else if (window.defaultProducts) {
        setProducts(window.defaultProducts);
      }
    } catch (e) {
      if (window.defaultProducts) {
        setProducts(window.defaultProducts);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await window.apiClient.get('categories.php');
      if (res.success && res.data) {
        setCategories(res.data);
      }
    } catch (e) {
      console.warn('Failed to load categories', e);
    }
  };

  const handleOpenAddModal = () => {
    setEditMode(false);
    setModalTab('general');
    setForm({
      id: 0,
      product_code: `PRD-${Date.now().toString().slice(-4)}`,
      name: '',
      category_id: 0,
      badge: 'High Precision',
      short_description: '',
      full_description: '',
      applications: [
        'End-of-line functional safety validation',
        'Routine maintenance and overhaul calibration',
        'Type testing and design qualification'
      ],
      testingParameters: [
        { name: 'Supply Pressure', unit: '0 - 15 Bar', icon: 'speed' },
        { name: 'Micro-Leakage Decay', unit: '< 0.01 Bar/min', icon: 'leak_add' },
        { name: 'Response Time', unit: '± 2 ms', icon: 'timer' }
      ],
      howItWorks: [
        { step: '01', title: 'Pneumatic / Hydraulic Clamping', desc: 'Secure component into precision manifold fixture with automated sensor lock.' },
        { step: '02', title: 'Automated Pressure Stepping', desc: 'PLC steps input line pressure according to predefined testing recipe.' },
        { step: '03', title: 'High-Speed DAQ Sampling', desc: '24-bit transducers capture dynamic response curves at 10 kHz.' },
        { step: '04', title: 'Pass / Fail Verdict & SQL Log', desc: 'Real-time curve comparison against tolerance envelopes with digital certificate generation.' }
      ],
      specs: {
        'Operating Pressure Range': '0 – 16 Bar (Pneumatic) / Up to 350 Bar (Hydraulic)',
        'Accuracy Class': '±0.05% Full Scale',
        'Control Architecture': 'Siemens S7-1500 PLC + Industrial PC HMI',
        'Data Acquisition': '24-Bit High-Speed Synchronous DAQ',
        'Power Supply': '415V AC, 3-Phase, 50 Hz'
      },
      sensorsInstrumentation: [
        'Piezo-resistive high-frequency pressure transducers',
        'Thermal mass differential micro-leakage flow sensors',
        'High-precision optical / LVDT linear displacement transducers'
      ],
      automationSoftware: [
        'Siemens S7-1500 / Beckhoff TwinCAT PLC Automation',
        'Custom Windows-based DAQ & Calibration GUI with Recipe Management',
        'Automated PDF Calibration Certificate Generation with SQL Logging'
      ],
      standardsCompliance: ['UIC 540 / 541', 'RDSO Spec', 'ISO 9001:2015', 'CE Certified'],
      primary_image: '/api/storage/banners/hero_railways.png',
      gallery: [],
      datasheet_url: '',
      status: 'published',
      display_order: products.length + 1
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (product) => {
    setEditMode(true);
    setModalTab('general');
    
    // Parse specs and deep dossier fields if serialized
    let parsedSpecs = {};
    if (product.specs) parsedSpecs = product.specs;
    else if (product.specs_json) {
      try { parsedSpecs = typeof product.specs_json === 'string' ? JSON.parse(product.specs_json) : product.specs_json; } catch(e) {}
    }

    setForm({
      id: product.id,
      product_code: product.product_code || product.id,
      name: product.name || product.title,
      category_id: product.category_id || 1,
      badge: product.badge || 'High Precision',
      short_description: product.short_description || product.shortDesc || '',
      full_description: product.full_description || product.description || '',
      applications: product.applications || [
        'End-of-line production acceptance testing',
        'Overhaul workshop calibration & validation'
      ],
      testingParameters: product.testingParameters || [
        { name: 'Line Pressure', unit: '0 - 15 Bar', icon: 'speed' },
        { name: 'Leakage Rate', unit: '0.01 Bar/min', icon: 'leak_add' }
      ],
      howItWorks: product.howItWorks || [
        { step: '01', title: 'Component Nesting', desc: 'Secure component into quick-clamping fixture.' },
        { step: '02', title: 'Automated Cycle', desc: 'Execute multi-step pressure and functional validation test.' }
      ],
      specs: parsedSpecs,
      sensorsInstrumentation: product.sensorsInstrumentation || [
        'Piezo-resistive pressure transducers with NABL calibration'
      ],
      automationSoftware: product.automationSoftware || [
        'PLC Closed-Loop Control with SCADA Touchscreen'
      ],
      standardsCompliance: product.standardsCompliance || ['UIC 540', 'RDSO', 'ISO 9001'],
      primary_image: product.primary_image || product.image || '/api/storage/banners/hero_railways.png',
      gallery: product.gallery || [],
      datasheet_url: product.datasheet_url || product.datasheetUrl || '',
      status: product.status || 'published',
      display_order: product.display_order || 0
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await window.apiClient.uploadFile('media.php', formData);
      if (res.success && res.url) {
        setForm(prev => ({ ...prev, primary_image: res.url }));
        showNotification('Primary machine image uploaded.');
      }
    } catch (err) {
      showNotification('Image upload error.', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await window.apiClient.uploadFile('media.php', formData);
      if (res.success && res.url) {
        setForm(prev => ({ ...prev, gallery: [...(prev.gallery || []), res.url] }));
        showNotification('Gallery image added.');
      }
    } catch (err) {
      showNotification('Upload failed.', 'error');
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleDatasheetUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingDatasheet(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await window.apiClient.uploadFile('media.php', formData);
      if (res.success && res.url) {
        setForm(prev => ({ ...prev, datasheet_url: res.url }));
        showNotification('Datasheet PDF uploaded.');
      }
    } catch (err) {
      showNotification('Datasheet upload error.', 'error');
    } finally {
      setUploadingDatasheet(false);
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!form.name) {
      showNotification('Product Name is required.', 'error');
      return;
    }

    try {
      const payload = {
        ...form,
        specs_json: JSON.stringify(form.specs)
      };

      if (editMode) {
        await window.apiClient.put('products.php', payload);
        showNotification(`Product '${form.name}' updated successfully.`);
      } else {
        await window.apiClient.post('products.php', payload);
        showNotification(`Product '${form.name}' created successfully.`);
      }
      setShowModal(false);
      fetchProducts();
      window.dispatchEvent(new Event('novatek_content_updated'));
    } catch (err) {
      showNotification(err.message || 'Failed to save product.', 'error');
    }
  };

  const handleDeleteProduct = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete '${name}'?`)) return;
    try {
      await window.apiClient.delete('products.php', { id });
      showNotification(`Product '${name}' deleted.`);
      fetchProducts();
      window.dispatchEvent(new Event('novatek_content_updated'));
    } catch (err) {
      showNotification(err.message || 'Deletion failed.', 'error');
    }
  };

  const filteredProducts = products.filter((p) => {
    const pName = p.name || p.title || '';
    const pCat = p.category_name || p.category || '';
    const matchesCat = selectedCategory === 'All' || pCat === selectedCategory;
    const matchesSearch = pName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (p.product_code && p.product_code.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCat && matchesSearch;
  });

  return (
    <>
      <div className="space-y-6 animate-fadeIn">
      
      {/* Top Header */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-sans text-2xl font-bold text-slate-900">Products catalog</h1>
          <p className="font-sans text-xs text-slate-500">Manage deep engineering dossiers, test parameters measured, operational workflows, and technical specifications.</p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold uppercase px-4 py-2.5 rounded-xl flex items-center gap-2 shadow transition-colors"
        >
          <span className="material-symbols-outlined text-lg">add_circle</span>
          <span>create new products</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-sm">
        <div className="relative w-full sm:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
          <input
            type="text"
            placeholder="Search test rigs by model or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs outline-none focus:border-secondary"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto w-full sm:w-auto">
          {['All', ...categories.map(c => c.name)].map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold whitespace-nowrap transition-colors ${
                selectedCategory === cat ? 'bg-secondary text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 font-mono text-xs">Loading machine catalogue...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 text-slate-500 font-sans text-sm">
          No test systems found matching your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((prod) => (
            <div key={prod.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm flex flex-col justify-between group">
              <div>
                <div className="h-44 bg-slate-900 overflow-hidden relative">
                  <img
                    src={prod.primary_image || prod.image || '/api/storage/banners/hero_railways.png'}
                    alt={prod.name || prod.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 bg-slate-900/80 text-secondary text-[10px] font-mono font-bold px-2 py-0.5 rounded border border-orange-500/30">
                    {prod.category_name || prod.category || 'Test System'}
                  </span>
                  <span className="absolute bottom-3 right-3 bg-slate-900/80 text-white text-[10px] font-mono px-2 py-0.5 rounded">
                    {prod.product_code || `ID: ${prod.id}`}
                  </span>
                </div>

                <div className="p-5 space-y-2">
                  <h3 className="font-sans text-base font-bold text-slate-900 line-clamp-2">
                    {prod.name || prod.title}
                  </h3>
                  <p className="font-sans text-xs text-slate-600 line-clamp-2">
                    {prod.short_description || prod.shortDesc}
                  </p>
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center text-xs font-mono font-bold">
                <button
                  onClick={() => handleOpenEditModal(prod)}
                  className="text-slate-700 hover:text-secondary flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-sm">edit_note</span>
                  <span>Edit Dossier</span>
                </button>
                <button
                  onClick={() => handleDeleteProduct(prod.id, prod.name || prod.title)}
                  className="text-red-600 hover:text-red-800 flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-sm">delete</span>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Engineering Dossier Modal Editor */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-5xl w-full flex flex-col max-h-[92vh] border border-slate-200 shadow-2xl relative">
            
            {/* Modal Header Container */}
            <div className="flex flex-col shrink-0 p-6 sm:px-10 sm:pt-10 sm:pb-2 space-y-4 border-b border-slate-200">
              {/* Modal Header */}
              <div className="flex justify-between items-center">
              <div>
                <span className="font-mono text-[11px] font-bold text-secondary uppercase block">
                  {editMode ? `Editing Machine Dossier #${form.id}` : 'New Machine Engineering Dossier'}
                </span>
                <h3 className="font-sans text-xl font-bold text-slate-900">
                  {form.name || 'Untitled Test Machine'}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700">
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>

            {/* Modal Sub-Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
              {[
                { id: 'general', label: '1. General Info', icon: 'info' },
                { id: 'applications', label: '2. Target Scope', icon: 'check_circle' },
                { id: 'parameters', label: '3. Test Parameters', icon: 'speed' },
                { id: 'workflow', label: '4. How It Operates', icon: 'account_tree' },
                { id: 'specs', label: '5. Specifications Grid', icon: 'table_chart' },
                { id: 'sensors', label: '6. Sensors & Standards', icon: 'sensors' },
                { id: 'media', label: '7. Photos & Datasheet', icon: 'perm_media' }
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setModalTab(t.id)}
                  className={`px-3 py-2 rounded-xl text-xs font-mono font-bold whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                    modalTab === t.id ? 'bg-secondary text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
              </div>
            </div>
            
            {/* Scrollable Body */}
            <div className="p-6 sm:p-10 overflow-y-auto custom-scrollbar flex-1 space-y-6">

            {/* Tab 1: General Info */}
            {modalTab === 'general' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">Product / Machine Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Universal Railway Brake Valve Test Rig"
                      className="w-full p-2.5 text-xs border border-slate-300 rounded-xl outline-none focus:border-secondary"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">Model / Product Code</label>
                    <input
                      type="text"
                      value={form.product_code}
                      onChange={(e) => setForm({ ...form, product_code: e.target.value })}
                      placeholder="e.g. NVT-RBV-500"
                      className="w-full p-2.5 text-xs border border-slate-300 rounded-xl outline-none focus:border-secondary font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">Sector / Category</label>
                    <select
                      value={form.category_id}
                      onChange={(e) => setForm({ ...form, category_id: parseInt(e.target.value) })}
                      className="w-full p-2.5 text-xs border border-slate-300 rounded-xl outline-none focus:border-secondary bg-white"
                    >
                      <option value={0}>General Product (Uncategorized)</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">Badge Tag</label>
                    <input
                      type="text"
                      value={form.badge}
                      onChange={(e) => setForm({ ...form, badge: e.target.value })}
                      placeholder="e.g. UIC 540 / RDSO Compliant"
                      className="w-full p-2.5 text-xs border border-slate-300 rounded-xl outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">Publication Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="w-full p-2.5 text-xs border border-slate-300 rounded-xl outline-none bg-white font-mono"
                    >
                      <option value="published">Published (Public)</option>
                      <option value="draft">Draft (Hidden)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono font-bold text-slate-700 mb-1">Short Description (Catalogue summary)</label>
                  <textarea
                    rows="2"
                    value={form.short_description}
                    onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                    placeholder="Short 2-sentence summary..."
                    className="w-full p-2.5 text-xs border border-slate-300 rounded-xl outline-none focus:border-secondary"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-xs font-mono font-bold text-slate-700 mb-1">Full Engineering Overview</label>
                  <textarea
                    rows="4"
                    value={form.full_description}
                    onChange={(e) => setForm({ ...form, full_description: e.target.value })}
                    placeholder="Detailed engineering capabilities, pneumatic circuits, servo actuation, and safety interlocks..."
                    className="w-full p-2.5 text-xs border border-slate-300 rounded-xl outline-none focus:border-secondary"
                  ></textarea>
                </div>
              </div>
            )}

            {/* Tab 2: Applications */}
            {modalTab === 'applications' && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newAppInput}
                    onChange={(e) => setNewAppInput(e.target.value)}
                    placeholder="Add target component application (e.g. Distributor valve testing)..."
                    className="flex-grow p-2.5 text-xs border border-slate-300 rounded-xl outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!newAppInput) return;
                      setForm({ ...form, applications: [...form.applications, newAppInput] });
                      setNewAppInput('');
                    }}
                    className="bg-secondary text-white text-xs font-mono font-bold px-4 rounded-xl"
                  >
                    Add Scope
                  </button>
                </div>

                <div className="space-y-2">
                  {form.applications.map((app, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs font-sans">
                      <span className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-secondary text-base">check_circle</span>
                        <span>{app}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, applications: form.applications.filter((_, i) => i !== idx) })}
                        className="text-red-500 hover:text-red-700"
                      >
                        <span className="material-symbols-outlined text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tab 3: Testing Parameters */}
            {modalTab === 'parameters' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <input
                    type="text"
                    value={newParamName}
                    onChange={(e) => setNewParamName(e.target.value)}
                    placeholder="Parameter (e.g. Line Pressure)"
                    className="p-2 text-xs border border-slate-300 rounded-lg bg-white outline-none"
                  />
                  <input
                    type="text"
                    value={newParamUnit}
                    onChange={(e) => setNewParamUnit(e.target.value)}
                    placeholder="Value / Range (e.g. 0 - 15 Bar)"
                    className="p-2 text-xs border border-slate-300 rounded-lg bg-white outline-none font-mono"
                  />
                  <input
                    type="text"
                    value={newParamIcon}
                    onChange={(e) => setNewParamIcon(e.target.value)}
                    placeholder="Icon (e.g. speed, timer)"
                    className="p-2 text-xs border border-slate-300 rounded-lg bg-white outline-none font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!newParamName) return;
                      setForm({
                        ...form,
                        testingParameters: [
                          ...form.testingParameters,
                          { name: newParamName, unit: newParamUnit, icon: newParamIcon || 'speed' }
                        ]
                      });
                      setNewParamName('');
                      setNewParamUnit('');
                    }}
                    className="bg-secondary text-white text-xs font-mono font-bold px-3 py-2 rounded-lg"
                  >
                    Add Parameter
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {form.testingParameters.map((param, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-200 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-secondary text-lg">{param.icon || 'speed'}</span>
                        <div>
                          <strong className="block text-slate-900">{param.name}</strong>
                          <span className="font-mono text-secondary font-bold">{param.unit}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, testingParameters: form.testingParameters.filter((_, i) => i !== idx) })}
                        className="text-red-500 hover:text-red-700"
                      >
                        <span className="material-symbols-outlined text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tab 4: Workflow Steps */}
            {modalTab === 'workflow' && (
              <div className="space-y-4">
                <div className="space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <span className="font-mono text-xs font-bold text-slate-800 uppercase block">Add Operational Workflow Step</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newStepTitle}
                      onChange={(e) => setNewStepTitle(e.target.value)}
                      placeholder="Step Title (e.g. Automated Seal Clamping)"
                      className="p-2 text-xs border border-slate-300 rounded-lg bg-white"
                    />
                    <input
                      type="text"
                      value={newStepDesc}
                      onChange={(e) => setNewStepDesc(e.target.value)}
                      placeholder="Step Description (e.g. Pneumatic cylinder engages poka-yoke manifold)"
                      className="p-2 text-xs border border-slate-300 rounded-lg bg-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!newStepTitle) return;
                      const nextNum = (form.howItWorks.length + 1).toString().padStart(2, '0');
                      setForm({
                        ...form,
                        howItWorks: [
                          ...form.howItWorks,
                          { step: nextNum, title: newStepTitle, desc: newStepDesc }
                        ]
                      });
                      setNewStepTitle('');
                      setNewStepDesc('');
                    }}
                    className="bg-secondary text-white text-xs font-mono font-bold px-4 py-2 rounded-lg"
                  >
                    Add Operational Step
                  </button>
                </div>

                <div className="space-y-2">
                  {form.howItWorks.map((step, idx) => (
                    <div key={idx} className="flex justify-between items-start bg-white p-4 rounded-xl border border-slate-200 text-xs">
                      <div>
                        <span className="font-mono text-[11px] font-bold text-secondary uppercase">Step {step.step || idx + 1}</span>
                        <h4 className="font-sans font-bold text-slate-900 mt-0.5">{step.title}</h4>
                        <p className="text-slate-600 text-xs mt-1">{step.desc}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, howItWorks: form.howItWorks.filter((_, i) => i !== idx) })}
                        className="text-red-500 hover:text-red-700"
                      >
                        <span className="material-symbols-outlined text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tab 5: Specifications Grid */}
            {modalTab === 'specs' && (
              <div className="space-y-4">
                <div className="flex gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <input
                    type="text"
                    value={specKey}
                    onChange={(e) => setSpecKey(e.target.value)}
                    placeholder="Specification Name (e.g. Operating Pressure)"
                    className="flex-1 p-2 text-xs border border-slate-300 rounded-lg bg-white"
                  />
                  <input
                    type="text"
                    value={specVal}
                    onChange={(e) => setSpecVal(e.target.value)}
                    placeholder="Specification Value (e.g. 0 – 16 Bar)"
                    className="flex-1 p-2 text-xs border border-slate-300 rounded-lg bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!specKey || !specVal) return;
                      setForm({ ...form, specs: { ...form.specs, [specKey]: specVal } });
                      setSpecKey('');
                      setSpecVal('');
                    }}
                    className="bg-secondary text-white text-xs font-mono font-bold px-4 rounded-lg"
                  >
                    Add Spec
                  </button>
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs font-sans text-left">
                    <thead className="bg-slate-100 font-mono text-[11px] text-slate-700 uppercase">
                      <tr>
                        <th className="p-3">Specification Parameter</th>
                        <th className="p-3">Engineering Tolerance / Value</th>
                        <th className="p-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {Object.entries(form.specs).map(([k, v], idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-3 font-bold text-slate-900">{k}</td>
                          <td className="p-3 text-slate-700">{v}</td>
                          <td className="p-3 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                const copy = { ...form.specs };
                                delete copy[k];
                                setForm({ ...form, specs: copy });
                              }}
                              className="text-red-500 hover:text-red-700 text-xs font-mono font-bold"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tab 6: Sensors & Standards */}
            {modalTab === 'sensors' && (
              <div className="space-y-6">
                {/* Sensors */}
                <div className="space-y-3">
                  <span className="font-mono text-xs font-bold text-slate-800 uppercase block">1. Instrumentation & Sensors</span>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSensorInput}
                      onChange={(e) => setNewSensorInput(e.target.value)}
                      placeholder="Add sensor (e.g. Piezo-resistive pressure transducers)"
                      className="flex-grow p-2 text-xs border border-slate-300 rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!newSensorInput) return;
                        setForm({ ...form, sensorsInstrumentation: [...form.sensorsInstrumentation, newSensorInput] });
                        setNewSensorInput('');
                      }}
                      className="bg-secondary text-white text-xs font-mono font-bold px-4 rounded-xl"
                    >
                      Add
                    </button>
                  </div>
                  <ul className="space-y-1.5 text-xs text-slate-700">
                    {form.sensorsInstrumentation.map((s, i) => (
                      <li key={i} className="flex justify-between items-center bg-slate-50 p-2.5 rounded-lg">
                        <span>• {s}</span>
                        <button type="button" onClick={() => setForm({ ...form, sensorsInstrumentation: form.sensorsInstrumentation.filter((_, idx) => idx !== i) })} className="text-red-500">×</button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Standards */}
                <div className="space-y-3 pt-3 border-t border-slate-100">
                  <span className="font-mono text-xs font-bold text-slate-800 uppercase block">2. Compliance Standards (UIC, RDSO, ISO, SAE)</span>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newStandardInput}
                      onChange={(e) => setNewStandardInput(e.target.value)}
                      placeholder="Add standard (e.g. UIC 540, RDSO 01-MP)"
                      className="flex-grow p-2 text-xs border border-slate-300 rounded-xl font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!newStandardInput) return;
                        setForm({ ...form, standardsCompliance: [...form.standardsCompliance, newStandardInput] });
                        setNewStandardInput('');
                      }}
                      className="bg-secondary text-white text-xs font-mono font-bold px-4 rounded-xl"
                    >
                      Add Standard
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form.standardsCompliance.map((std, i) => (
                      <span key={i} className="bg-orange-50 border border-orange-200 text-secondary text-xs font-mono font-bold px-2.5 py-1 rounded flex items-center gap-1.5">
                        <span>{std}</span>
                        <button type="button" onClick={() => setForm({ ...form, standardsCompliance: form.standardsCompliance.filter((_, idx) => idx !== i) })} className="text-red-500 hover:text-red-700">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tab 7: Media & Datasheet */}
            {modalTab === 'media' && (
              <div className="space-y-6">
                {/* Primary Image */}
                <div className="space-y-2">
                  <label className="block text-xs font-mono font-bold text-slate-700">Primary Machine Cover Photograph</label>
                  <div className="flex items-center gap-4">
                    <div className="h-20 w-32 bg-slate-900 rounded-xl overflow-hidden border border-slate-300 shrink-0">
                      <img 
                        src={window.resolveMediaUrl(form.primary_image, 'machine')} 
                        alt="Preview" 
                        onError={(e) => window.handleImageError(e, 'machine')}
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    <div className="flex-grow space-y-2">
                      <input
                        type="text"
                        value={form.primary_image}
                        onChange={(e) => setForm({ ...form, primary_image: e.target.value })}
                        placeholder="Image URL or upload..."
                        className="w-full p-2 text-xs border border-slate-300 rounded-xl font-mono"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setMediaPickerConfig({
                            isOpen: true,
                            title: 'Choose Machine Cover Image',
                            onSelect: (url) => setForm({ ...form, primary_image: url })
                          })}
                          className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-sans text-xs font-bold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <span className="material-symbols-outlined text-sm text-secondary">photo_library</span>
                          <span>Choose from Media</span>
                        </button>
                        <input type="file" ref={imageUploadRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                        <button
                          type="button"
                          disabled={uploadingImage}
                          onClick={() => imageUploadRef.current && imageUploadRef.current.click()}
                          className="bg-slate-900 hover:bg-secondary text-white font-sans text-xs font-bold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <span className="material-symbols-outlined text-sm">upload</span>
                          <span>{uploadingImage ? 'Uploading...' : 'Upload Image'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Gallery Images */}
                <div className="space-y-3 pt-4 border-t border-slate-100">
                  <label className="block text-xs font-mono font-bold text-slate-700">Additional Gallery Images</label>
                  
                  {/* Current Gallery List */}
                  {form.gallery && form.gallery.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {form.gallery.map((url, idx) => (
                        <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-200 h-24 bg-slate-900">
                          <img 
                            src={window.resolveMediaUrl(url, 'machine')} 
                            alt={`Gallery ${idx}`}
                            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                            onError={(e) => window.handleImageError(e, 'machine')}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newGallery = [...form.gallery];
                              newGallery.splice(idx, 1);
                              setForm({ ...form, gallery: newGallery });
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                            title="Remove Image"
                          >
                            <span className="material-symbols-outlined text-[14px]">close</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Gallery Image */}
                  <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                    <input
                      type="text"
                      value={newGalleryInput}
                      onChange={(e) => setNewGalleryInput(e.target.value)}
                      placeholder="Image URL to add..."
                      className="flex-grow p-2.5 text-xs border border-slate-300 rounded-xl font-mono"
                    />
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          if (newGalleryInput.trim()) {
                            setForm({ ...form, gallery: [...(form.gallery || []), newGalleryInput.trim()] });
                            setNewGalleryInput('');
                          }
                        }}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-sans text-xs font-bold px-3 py-2.5 rounded-xl transition-all flex items-center gap-1"
                      >
                        <span className="material-symbols-outlined text-sm">add</span>
                        <span>Add URL</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMediaPickerConfig({
                          isOpen: true,
                          title: 'Choose Gallery Image',
                          onSelect: (url) => setForm({ ...form, gallery: [...(form.gallery || []), url] })
                        })}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-sans text-xs font-bold px-3 py-2.5 rounded-xl transition-all flex items-center gap-1"
                      >
                        <span className="material-symbols-outlined text-sm text-secondary">photo_library</span>
                        <span>Media</span>
                      </button>
                      <input type="file" ref={galleryUploadRef} onChange={handleGalleryUpload} accept="image/*" className="hidden" />
                      <button
                        type="button"
                        disabled={uploadingGallery}
                        onClick={() => galleryUploadRef.current && galleryUploadRef.current.click()}
                        className="bg-slate-900 hover:bg-secondary text-white font-sans text-xs font-bold px-3 py-2.5 rounded-xl transition-all flex items-center gap-1"
                      >
                        <span className="material-symbols-outlined text-sm">upload</span>
                        <span>{uploadingGallery ? 'Uploading...' : 'Upload'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Technical Datasheet PDF */}
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <label className="block text-xs font-mono font-bold text-slate-700">Technical Datasheet PDF File</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={form.datasheet_url}
                      onChange={(e) => setForm({ ...form, datasheet_url: e.target.value })}
                      placeholder="Datasheet PDF URL or upload..."
                      className="flex-grow p-2 text-xs border border-slate-300 rounded-xl font-mono"
                    />
                    <input type="file" ref={datasheetUploadRef} onChange={handleDatasheetUpload} accept=".pdf,.doc,.docx" className="hidden" />
                    <button
                      type="button"
                      disabled={uploadingDatasheet}
                      onClick={() => datasheetUploadRef.current && datasheetUploadRef.current.click()}
                      className="bg-slate-900 text-white text-xs font-mono font-bold px-4 py-2.5 rounded-xl whitespace-nowrap"
                    >
                      {uploadingDatasheet ? 'Uploading...' : 'Upload PDF Datasheet'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            </div>
            {/* Bottom Actions */}
            <div className="p-6 sm:px-10 border-t border-slate-200 flex justify-end gap-3 shrink-0 bg-slate-50 rounded-b-3xl">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 text-xs font-mono font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveProduct}
                className="bg-secondary hover:bg-orange-600 text-white px-6 py-2.5 text-xs font-mono font-bold uppercase rounded-xl shadow"
              >
                Save Machine Dossier
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Reusable Global Media Picker */}
      {window.MediaPickerModal && (
        <window.MediaPickerModal
          isOpen={mediaPickerConfig.isOpen}
          title={mediaPickerConfig.title}
          onClose={() => setMediaPickerConfig({ ...mediaPickerConfig, isOpen: false })}
          onSelect={(url) => {
            if (mediaPickerConfig.onSelect) mediaPickerConfig.onSelect(url);
          }}
        />
      )}
    </>
  );
};
